import React from 'react';
import { type NavItemNoIcon } from '@cx-web/domain-types';
import classnames from 'classnames';

import Icon from '../../../../../primitives/icon/index';
import OCButton from '../../OCButton/index';
import { getCountriesWithSubdivisions } from '../utils/getCountriesWithSubdivisions';

import styles from './styles/DesktopDropdownLegacy.module.css';

interface DesktopStateDropdownLegacyProps {
  firstLevelNav: NavItemNoIcon[];
  selectedState: string;
  setSelectedState: (state: string) => void;
  stateOptions: Array<{ code: string; label: string }>;
}

// Legacy desktop state dropdown — the pre-6cb4313d implementation. Uses CSS hover to open the panel
// rather than click + JS state, and renders an indicator triangle via an inline character rather than
// the animated chevron used in the new DesktopDropdown.
const DesktopStateDropdownLegacy: React.FC<DesktopStateDropdownLegacyProps> = ({
  firstLevelNav,
  selectedState,
  setSelectedState,
  stateOptions,
}) => {
  const countries = getCountriesWithSubdivisions(firstLevelNav);

  if (countries?.length === 0) {
    return null;
  }

  const supportedStateSet = new Set(stateOptions.map(state => state.code));

  const isSingleColumn = stateOptions.length < 11;

  return (
    <div
      className={classnames(styles.stateDropdown, isSingleColumn ? styles.singleColumn : '')}
      data-testid="desktop-states"
    >
      <div className={styles.cta}>
        {selectedState} <div className={styles.indicator}>&#x25BE;</div>
      </div>
      <div className={classnames(styles.states, isSingleColumn ? styles.singleColumn : '')}>
        {countries.map(country => {
          const subdivisions = Object.entries(country.subdivisions)
            .filter(([code]) => supportedStateSet.has(code))
            .map(([code, details]) => ({
              code,
              name: details.name,
            }));

          if (!subdivisions.length) {
            return null;
          }

          const tableCount = country.name === 'United States' ? 10 : 5;
          const columnCount = Math.ceil(subdivisions.length / tableCount);
          const rowCount = Math.min(subdivisions.length, tableCount);

          return (
            <React.Fragment key={country.name}>
              <div className={styles.countryTitle} data-testid="country-title">
                {country.name}
              </div>
              <div
                className={styles.stateGrid}
                data-testid="state-grid"
                style={{
                  gridTemplateColumns: `repeat(${columnCount}, ${435 / columnCount}px)`,
                  gridTemplateRows: `repeat(${rowCount}, 1fr)`,
                }}
              >
                {subdivisions.map(subdivision => {
                  return (
                    <OCButton
                      className={classnames(styles.state, subdivision.code === selectedState ? styles.selected : '')}
                      key={subdivision.code}
                      onClick={() => setSelectedState(subdivision.code)}
                    >
                      {subdivision.name}
                      <div className={styles.checkmark}>
                        <Icon fill="#00182e" icon="Checkmark" />
                      </div>
                    </OCButton>
                  );
                })}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default DesktopStateDropdownLegacy;
